import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";

import ModalForm, { Field, FieldType } from "../../../../components/ModalForm";
import ConfirmModal from "../../../../components/ConfirmModal";
import { bulkUpdateUsers } from "../../../../services/api/admin/bulkUsers";
import {
    assignUsersToPromotion,
    getPromotionOptions,
    readRepeatConfirmationUserIds,
    AssignPromotionResult,
    Promotion,
} from "../../../../services/api/admin/promotions";
import RepeatAssignConfirm from "../../../Admin/Promotions/RepeatAssignConfirm";
import { getAllRoles } from "../../../../services/api/admin/role";
import {
    addTagsToUsers,
    getAllTags,
    removeTagsFromUsers,
} from "../../../../services/api/admin/tags";
import { BulkUserAction } from "../../../../types/api/admin/bulkUsers";
import { Tag } from "../../../../types/api/admin/tag";
import { Role } from "../../../../types/Role";
import { isStaffRole } from "../../../../utils/graduation";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../../utils/errors";

export type SelectionBulkMode = BulkUserAction | "assign_promotion";

type Props = {
    isOpen: boolean;
    mode: SelectionBulkMode | null;
    selectedUserIds: string[];
    onSubmit?: () => void;
    onClose: () => void;
};

const MODE_TITLES: Record<SelectionBulkMode, string> = {
    change_role: "Changer le rôle",
    add_tag: "Ajouter des tags",
    remove_tag: "Retirer des tags",
    deactivate: "Désactiver les comptes",
    assign_promotion: "Attribuer à une promotion",
};

const SelectionBulkModal: FC<Props> = ({
    isOpen,
    mode,
    selectedUserIds,
    onSubmit,
    onClose,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [tags, setTags] = useState<Tag[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
    const [roleId, setRoleId] = useState("");
    const [promotionId, setPromotionId] = useState("");
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});
    const [repeatPrompt, setRepeatPrompt] = useState<{
        count: number;
        resolve: (result: AssignPromotionResult | undefined) => void;
        reject: (error: unknown) => void;
    } | null>(null);
    const [repeatLoading, setRepeatLoading] = useState(false);

    useEffect(() => {
        if (!isOpen || !mode) return;
        setIsLoading(true);
        setSelectedTagIds([]);
        setRoleId("");
        setPromotionId("");
        setFieldErrorsState({});

        const load =
            mode === "change_role"
                ? getAllRoles().then((loaded) => setRoles(loaded ?? []))
                : mode === "add_tag" || mode === "remove_tag"
                  ? getAllTags().then((loaded) => setTags(loaded ?? []))
                  : mode === "assign_promotion"
                    ? getPromotionOptions().then((loaded) =>
                          setPromotions(loaded ?? [])
                      )
                    : Promise.resolve();

        load
            .catch(() => toast.error("Erreur lors du chargement"))
            .finally(() => setIsLoading(false));
    }, [isOpen, mode]);

    if (!mode) return null;

    const handleClose = () => {
        setSelectedTagIds([]);
        setRoleId("");
        setPromotionId("");
        setFieldErrorsState({});
        onClose();
    };

    const handleApply = async () => {
        if (selectedUserIds.length === 0) {
            toast.error("Aucun utilisateur sélectionné");
            return;
        }

        try {
            if (mode === "add_tag") {
                const tagErrors = setFieldErrors([
                    "tags",
                    selectedTagIds.length === 0 && REQUIRED_FIELD,
                ]);
                setFieldErrorsState(tagErrors);
                if (
                    notifyFieldErrors(tagErrors, {
                        message: "Veuillez sélectionner au moins un tag",
                        type: "error",
                    })
                ) {
                    return;
                }
                await addTagsToUsers(selectedUserIds, selectedTagIds);
                toast.success("Tags ajoutés avec succès");
            } else if (mode === "remove_tag") {
                const tagErrors = setFieldErrors([
                    "tags",
                    selectedTagIds.length === 0 && REQUIRED_FIELD,
                ]);
                setFieldErrorsState(tagErrors);
                if (
                    notifyFieldErrors(tagErrors, {
                        message: "Veuillez sélectionner au moins un tag",
                        type: "error",
                    })
                ) {
                    return;
                }
                await removeTagsFromUsers(selectedUserIds, selectedTagIds);
                toast.success("Tags retirés avec succès");
            } else if (mode === "change_role") {
                const roleErrors = setFieldErrors([
                    "roleId",
                    !roleId && REQUIRED_FIELD,
                ]);
                setFieldErrorsState(roleErrors);
                if (
                    notifyFieldErrors(roleErrors, {
                        message: "Veuillez sélectionner un rôle",
                        type: "error",
                    })
                ) {
                    return;
                }
                const result = await bulkUpdateUsers({
                    userIds: selectedUserIds,
                    action: "change_role",
                    roleId,
                });
                if (!result) {
                    toast.error("Erreur lors du changement de rôle");
                    return;
                }
                toast.success(
                    `${result.updatedCount} utilisateur(s) mis à jour`
                );
            } else if (mode === "deactivate") {
                const result = await bulkUpdateUsers({
                    userIds: selectedUserIds,
                    action: "deactivate",
                });
                if (!result) {
                    toast.error("Erreur lors de la désactivation");
                    return;
                }
                toast.success(
                    `${result.updatedCount} compte(s) désactivé(s)`
                );
            } else if (mode === "assign_promotion") {
                const promoErrors = setFieldErrors([
                    "promotionId",
                    !promotionId && REQUIRED_FIELD,
                ]);
                setFieldErrorsState(promoErrors);
                if (
                    notifyFieldErrors(promoErrors, {
                        message: "Veuillez sélectionner une promotion",
                        type: "error",
                    })
                ) {
                    return;
                }
                const result = await new Promise<
                    AssignPromotionResult | undefined
                >((resolve, reject) => {
                    const attempt = async (countAsRepeat?: boolean) => {
                        try {
                            resolve(
                                await assignUsersToPromotion(
                                    promotionId,
                                    selectedUserIds,
                                    countAsRepeat
                                )
                            );
                        } catch (error) {
                            const changingIds =
                                readRepeatConfirmationUserIds(error);
                            if (!changingIds || countAsRepeat !== undefined) {
                                reject(error);
                                return;
                            }
                            setRepeatPrompt({
                                count: changingIds.length,
                                resolve,
                                reject,
                            });
                        }
                    };
                    void attempt();
                });
                if (!result) return;
                const repeats = result.repeatsRecorded ?? 0;
                toast.success(
                    repeats > 0
                        ? `${result.updated} membre(s) attribué(s) · ${repeats} redoublement${repeats > 1 ? "s" : ""}`
                        : `${result.updated} membre(s) attribué(s) à la promotion`
                );
            }

            setSelectedTagIds([]);
            setRoleId("");
            setPromotionId("");
            onSubmit?.();
            handleClose();
        } catch (error) {
            console.error("Selection bulk error:", error);
            toast.error("Erreur lors de l'action groupée");
        }
    };

    if (mode === "deactivate") {
        return (
            <ConfirmModal
                open={isOpen}
                handleClose={handleClose}
                handleConfirm={handleApply}
                description={`Désactiver ${selectedUserIds.length} compte${
                    selectedUserIds.length > 1 ? "s" : ""
                } sélectionné${selectedUserIds.length > 1 ? "s" : ""} ?`}
            />
        );
    }

    const fields: Field<FieldType>[] =
        mode === "change_role"
            ? [
                  {
                      id: "roleId",
                      label: "Nouveau rôle",
                      type: "select",
                      value: roleId,
                      onChange: (value: string) => {
                          setRoleId(value);
                          setFieldErrorsState((prev) =>
                              clearFieldError(prev, "roleId")
                          );
                      },
                      error: fieldErrors.roleId,
                      options: [
                          { value: "", label: "-" },
                          ...roles
                              .filter((role) => !isStaffRole(role.name))
                              .map((role) => ({
                                  value: role.id,
                                  label: role.name,
                              })),
                      ],
                  },
              ]
            : mode === "assign_promotion"
              ? [
                    {
                        id: "promotionId",
                        label: "Promotion",
                        type: "select",
                        value: promotionId,
                        onChange: (value: string) => {
                            setPromotionId(value);
                            setFieldErrorsState((prev) =>
                                clearFieldError(prev, "promotionId")
                            );
                        },
                        error: fieldErrors.promotionId,
                        options: [
                            { value: "", label: "-" },
                            ...promotions.map((promo) => ({
                                value: promo.id,
                                label: promo.name,
                            })),
                        ],
                    },
                ]
              : [
                    {
                        id: "tags",
                        label: "Tags",
                        type: "select-multiple",
                        value: selectedTagIds,
                        onChange: (value: string[]) => {
                            setSelectedTagIds(value);
                            setFieldErrorsState((prev) =>
                                clearFieldError(prev, "tags")
                            );
                        },
                        error: fieldErrors.tags,
                        options: tags.map((tag) => ({
                            value: tag.id,
                            label: tag.name,
                        })),
                    },
                ];

    const finishRepeatChoice = async (countAsRepeat: boolean) => {
        if (!repeatPrompt) return;
        setRepeatLoading(true);
        try {
            const result = await assignUsersToPromotion(
                promotionId,
                selectedUserIds,
                countAsRepeat
            );
            repeatPrompt.resolve(result);
            setRepeatPrompt(null);
        } catch (error) {
            repeatPrompt.reject(error);
            setRepeatPrompt(null);
        } finally {
            setRepeatLoading(false);
        }
    };

    return (
        <>
            <RepeatAssignConfirm
                open={repeatPrompt !== null}
                count={repeatPrompt?.count ?? 0}
                loading={repeatLoading}
                onClose={() => {
                    if (repeatLoading) return;
                    repeatPrompt?.resolve(undefined);
                    setRepeatPrompt(null);
                }}
                onNo={() => {
                    void finishRepeatChoice(false);
                }}
                onYes={() => {
                    void finishRepeatChoice(true);
                }}
            />
            <ModalForm
                open={isOpen}
                title={MODE_TITLES[mode]}
                fields={fields}
                onSubmit={handleApply}
                handleClose={handleClose}
                size="small"
                isLoading={isLoading}
            />
        </>
    );
};

export default SelectionBulkModal;
